
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MapPin, Star } from 'lucide-react';

interface Location {
  id: string;
  name: string;
  county: string;
  population?: number;
}

interface BlogSidebarProps {
  selectedLocation: string;
  locations: Location[];
  locationSearchQuery: string;
  filteredLocations: Location[];
  onLocationChange: (location: string) => void;
  onLocationSearchChange: (query: string) => void;
}

const BlogSidebar: React.FC<BlogSidebarProps> = ({
  selectedLocation,
  locations,
  locationSearchQuery,
  filteredLocations,
  onLocationChange,
  onLocationSearchChange
}) => {
  return (
    <div className="w-full lg:w-1/3">
      <div className="sticky top-8 space-y-8">
        <Card className="border-brand-trophy-gold/30 shadow-lg">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-4 text-brand-charcoal-gray flex items-center gap-2">
              <MapPin className="h-5 w-5 text-brand-trophy-gold" />
              Location Search Tool
            </h3>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="search"
                  placeholder="Search locations..."
                  className="pl-10 border-2 border-gray-200 focus:border-brand-trophy-gold"
                  value={locationSearchQuery}
                  onChange={(e) => onLocationSearchChange(e.target.value)}
                />
              </div>
              
              <Select onValueChange={(value) => onLocationChange(value)}>
                <SelectTrigger className="border-2 border-gray-200 focus:border-brand-trophy-gold">
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="raleigh">Raleigh (Default)</SelectItem>
                  {filteredLocations.slice(0, 20).map(location => (
                    <SelectItem key={location.id} value={location.name.toLowerCase()}>
                      <div className="flex items-center justify-between w-full">
                        <span>{location.name}</span>
                        <Badge variant="secondary" className="ml-2 text-xs bg-brand-trophy-gold/10 text-brand-trophy-gold">
                          {location.county}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                <p>Current location: <strong className="text-brand-charcoal-gray">{selectedLocation.charAt(0).toUpperCase() + selectedLocation.slice(1)}</strong></p>
                <p className="mt-1">{filteredLocations.length} locations available</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-brand-safety-orange/30 shadow-lg bg-gradient-to-br from-brand-safety-orange/5 to-brand-trophy-gold/5">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-4 text-brand-charcoal-gray flex items-center gap-2">
              <Star className="h-5 w-5 text-brand-safety-orange" />
              Need Roofing Help?
            </h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Our team of experienced professionals is ready to assist with all your roofing needs in <strong className="text-brand-trophy-gold">{selectedLocation.charAt(0).toUpperCase() + selectedLocation.slice(1)}</strong>.
            </p>
            <Link to="/contact">
              <Button className="w-full bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold shadow-lg py-3">
                Get a Free Estimate
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BlogSidebar;
